import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Browser } from '@capacitor/browser'
import { App } from '@capacitor/app'
import { TokenFields, SigninReply, SignInRequest, CompleteSignupRequest, ChangeEmailReply } from '../shared/api_types';
import { JwtHelperService } from '@auth0/angular-jwt';
import { NavController, Platform } from '@ionic/angular';
import { Storage } from '@ionic/storage-angular';
import { BehaviorSubject, Observable } from 'rxjs';
import { getApiUrl } from '../shared/app_helpers';
import { AppService } from './app.service';
import { environment } from 'src/environments/environment';

const API_URL = getApiUrl() + '/api';

const REDIRECT_URL_APP = 'chargehq://app.chargehq.net'

const COGNITO_AUTH_ENDPOINT = environment.cognitoUrl + '/oauth2/authorize'
// the signup endpoint is undocumented, it's used when you click sign up
// on the Hosted UI
const COGNITO_SIGNUP_ENDPOINT = environment.cognitoUrl + '/signup'
const COGNITO_TOKEN_ENDPOINT = environment.cognitoUrl + '/oauth2/token'
const COGNITO_LOGOUT_ENDPOINT = environment.cognitoUrl + '/logout'
// the forgot password endpoint is undocumented
const COGNITO_FORGOT_ENDPOINT = environment.cognitoUrl + '/forgotPassword'

export enum OAuthGrantType {
  AuthorizationCode = 'authorization_code',
  RefreshToken = 'refresh_token',
}

// this data must be passed to the "state" parameter of the redirect uri
export interface RedirectState {
  nextRoute?: string //eg "/my-account/change-email"
}

export interface CognitoTokenReply {
  access_token: string
  id_token: string
  refresh_token: string
  token_type: string
  expires_in: number
}

export interface CognitoRefreshTokenReply {
  access_token: string
  id_token: string
  token_type: string
  expires_in: number
}

// api token
let _globalToken: string = null

export function getAuthToken(): string | null {
  return _globalToken
}

export function encodeRedirectState(state: RedirectState): string {
  return btoa(JSON.stringify(state))
}

export function decodeRedirectState(state: string): RedirectState {
  return JSON.parse(atob(state))
}

@Injectable({
  providedIn: 'root'
})
export class AuthService {

  private isLoggedInSubject: BehaviorSubject<boolean>
  private emailSubject: BehaviorSubject<string>
  private tokenFields?: TokenFields

  constructor(private jwtHelper: JwtHelperService,
    private platform: Platform,
    private app: AppService,
    private http: HttpClient,
    private navCtrl: NavController,
    private storage: Storage)
  {
    console.log(`AuthService using URL ${API_URL}`)
    this.storage.create()
    this.isLoggedInSubject = new BehaviorSubject(null)
    this.emailSubject = new BehaviorSubject(undefined)
    this.loadToken()
  
    // handle custom URL scheme open events eg chargehq://...
    App.addListener('appUrlOpen', data => {
      console.debug('auth: appUrlOpen', data.url)
      // the auth browser stays open on iOS so we close it here
      Browser.close()
      const path = data.url.replace(REDIRECT_URL_APP, '')
      console.debug('auth: navigating to', path)
      this.navCtrl.navigateRoot(path)
    })
  }

  // are we running as a mobile app?
  get isMobileApp(): boolean {
    return this.platform.is('hybrid')
  }
  
  // do we have a token? (observable)
  get isLoggedIn(): Observable<boolean> {
    return this.isLoggedInSubject.asObservable()
  }

  get email(): Observable<string> {
    return this.emailSubject.asObservable()
  }

  get isAdmin(): boolean | undefined {
    return this.tokenFields?.user?.isAdmin
  }

  getEmail() {
    return this.emailSubject.value
  }

  getTokenFields(): TokenFields | undefined {
    return this.tokenFields
  }

  private async loadToken() {
    const token = await this.storage.get('token')
    console.debug('AuthService loadToken', token)
    this.setToken(token, false)
  }

  // do we have a token? doesn't say if the token is valid or not
  hasToken(): boolean {
    return _globalToken != null
  }

  // do we have an impersonating token?
  isImpersonating(): boolean {
    return _globalToken && this.tokenFields.impersonatingUser != undefined
  }

  // open the given URL in a browser suitable for use with oauth
  private async openAuthPage(url: string) {
    if (this.isMobileApp) {
      // open the auth site using the system browser. The auth site will redirect
      // to the app link ie chargehq://auth-redirect
      Browser.open({
        url,
        presentationStyle: 'popover',
      })
    }
    else {
      // in the webapp, simply navigate to the authurl in the current tab.
      // the auth site will return to the webapp using the redirect URL
      window.open(url, '_self')
    }
  }

  async openAuthScreen(signUp: boolean, provider?: string) {
    const url = await this.getAuthUrl(signUp, {}, provider)
    console.debug('auth url', url)
    this.openAuthPage(url)
  }

  getAppUri(): string {
    return this.isMobileApp ? REDIRECT_URL_APP : environment.appUriWeb
  }

  getRedirectUri(): string {
    return this.getAppUri() + '/auth-redirect'
  }

  getLogoutUri(): string {
    return this.getAppUri() + '/logout-redirect'
  }

  async getAuthUrl(signUp: boolean, state: RedirectState, provider?: string): Promise<string> {
    // see https://docs.aws.amazon.com/cognito/latest/developerguide/authorization-endpoint.html
    const endpoint = signUp ? COGNITO_SIGNUP_ENDPOINT : COGNITO_AUTH_ENDPOINT
    const url = new URL(endpoint)
    url.searchParams.append('response_type', 'code')
    url.searchParams.append('client_id', environment.cognitoClientId)
    url.searchParams.append('redirect_uri', this.getRedirectUri())
    // put base64-encoded JSON in the state param
    console.debug('state', state)
    url.searchParams.append('state', btoa(JSON.stringify(state)))
    if (provider) {
      url.searchParams.append('identity_provider', provider)
    }
    //TODO state param for security?
    // scope: default is all scopes for client
    return url.toString()
  }

  getForgotPasswordUrl(email: string): string {
    // this endpoint is undocumented, it allows going straight to the forgot password screen in the UI
    const url = new URL(COGNITO_FORGOT_ENDPOINT)
    url.searchParams.append('response_type', 'code')
    url.searchParams.append('client_id', environment.cognitoClientId)
    url.searchParams.append('redirect_uri', this.getRedirectUri())
    if (email) {
      url.searchParams.append('username', email)
    }
    // scope: default is all scopes for client
    return url.toString()
  }

  // exchange an auth code for Cognito tokens
  async exchangeCodeForTokens(code: string) {
    const url = COGNITO_TOKEN_ENDPOINT
    const body = new HttpParams()
      .set('grant_type', OAuthGrantType.AuthorizationCode)
      .set('client_id', environment.cognitoClientId)
      .set('redirect_uri', this.getRedirectUri())
      .set('code', code)
    console.debug('exchangeCodeForTokens posting %s', url)
    const headers = {
      'Content-Type': 'application/x-www-form-urlencoded'
    }
    const res = await this.http.post(url, body.toString(), { headers }).toPromise()
    return res as CognitoTokenReply
  }

  async signInWithCode(code: string) {
    const cognitoTokens = await this.exchangeCodeForTokens(code)
    //await this.storeCognitoTokens(cognitoTokens)
    return await this.signIn(cognitoTokens.id_token)
  }

  // We don't store the cognito tokens, but I'm leaving this code here in case we do one day.
  // async loadCognitoTokens(): Promise<CognitoTokenReply | undefined> {
  //   const tokens = (await this.storage.get('cognitoTokens')) as CognitoTokenReply
  //   console.warn('tokens', tokens)
  //   return tokens
  // }

  // async storeCognitoTokens(cognitoTokens: CognitoTokenReply) {
  //   console.debug('auth: storing cognito tokens')
  //   await this.storage.set('cognitoTokens', cognitoTokens)
  // }

  // // obtain a new access token using the stored refresh token
  // async refreshCognitoTokens(): Promise<CognitoRefreshTokenReply> {
  //   const tokens = await this.loadCognitoTokens()
  //   if (tokens) {
  //     const url = COGNITO_TOKEN_ENDPOINT
  //     const body = new HttpParams()
  //       .set('grant_type', OAuthGrantType.RefreshToken)
  //       .set('client_id', environment.cognitoClientId)
  //       .set('refresh_token', tokens.refresh_token)
  //     console.debug('auth: refreshCognitoTokens')
  //     const headers = {
  //       'Content-Type': 'application/x-www-form-urlencoded'
  //     }
  //     const res = await this.http.post(url, body.toString(), { headers }).toPromise()
  //     return res as CognitoRefreshTokenReply
  //   }
  //   else {
  //     console.warn('auth: no stored cognito tokens')
  //     return null
  //   }
  // }

  // async getCognitoAccessToken(): Promise<string> {
  //   const tokens = await this.refreshCognitoTokens()
  //   return tokens.access_token
  // }

  // sign in with a Cognito ID token and obtain an api token
  async signIn(idToken: string): Promise<SigninReply> {
    const request: SignInRequest = {
      idToken,
      appVersion: await this.app.buildAppVersionInfo(),
    }
    const url = API_URL + '/signin'
    console.debug('posting to', url)
    const reply = (await this.http.post(url, request).toPromise()) as SigninReply
    this.processSigninReply(reply)
    return reply as SigninReply
  }

  // reauthenticate with Cognito and obtain fresh tokens
  async reauthenticate(nextRoute: string) {
    // open the Cognito logout endpoint with a redirect_uri which will
    // cause the Cognito login endpoint to be opened to allow reauth
    const url = new URL(COGNITO_LOGOUT_ENDPOINT)
    url.searchParams.append('client_id', environment.cognitoClientId)
    url.searchParams.append('response_type', 'code')
    url.searchParams.append('redirect_uri', this.getRedirectUri())
    url.searchParams.append('state', encodeRedirectState({ nextRoute }))
    this.openAuthPage(url.toString())
  }

  // open the Cognito forgot password endpoint
  async forgotPassword() {
    const email = this.getEmail()
    const url = this.getForgotPasswordUrl(email)
    this.openAuthPage(url)
  }

  async completeSignup(request: CompleteSignupRequest) {
    return this.http.post(`${API_URL}/complete-signup`, request).toPromise()
  }

  clearToken() {
    this.storage.clear()
    _globalToken = null
    this.isLoggedInSubject.next(false)
  }

  // remove the stored API token.
  // if cognitoLogout is true, open the Cognito logout endpoint which will
  // then redirect to the landing page
  // otherwise just go straight to the landing page
  async logout(cognitoLogout = false) {
    this.clearToken()
    if (cognitoLogout) {
      const url = new URL(COGNITO_LOGOUT_ENDPOINT)
      url.searchParams.append('client_id', environment.cognitoClientId)
      url.searchParams.append('logout_uri', this.getLogoutUri())
      this.openAuthPage(url.toString())
    }
    else {
      this.navCtrl.navigateRoot('/landing')
    }
  }

  async signoutAllDevices() {
    const url = API_URL + '/signout-all-devices'
    console.debug('posting to', url)
    await this.http.post(url, {}).toPromise()
    // we've invalidated our own token, so logout
    await this.logout(true)
  }

  async impersonate(email: string) {
    if (!this.isAdmin) {
      console.error('can not impersonate - not logged in as admin user')
      throw new Error('not logged in as admin user')
    }
    console.debug(`impersonating ${email}`)
    const res = await this.http.post(`${API_URL}/impersonate`, { email }).toPromise()
    const token = (res as any).token
    // adopt the token but don't store it
    this.setToken(token, false)
  }

  async stopImpersonating() {
    if (this.isImpersonating()) {
      console.debug('AuthService - stopping impersonation')
      await this.loadToken()
    }
  }

  async changeEmail(code: string) {
    const url = '/change-email'
    console.debug('posting', url, code)
    const res = (await this.http.post(`${API_URL}${url}`, { code }).toPromise()) as ChangeEmailReply
    // the change-email call returns a new API token with the new email address
    if (res.token) {
      console.debug('adopting new token')
      this.setToken(res.token, true)
    }
    else {
      console.warn('no token in reply')
    }
    return res
  }

  private processSigninReply(SigninReply: SigninReply) {
    console.debug('AuthService now signed in, storing token')
    //console.debug('token', SigninReply.apiToken)
    this.setToken(SigninReply.apiToken, true)
  }

  // adopt the given token, which may be null.
  // emits new values on the email subject and isLoggedIn subject
  private setToken(token: string, storeToken: boolean) {
    if (storeToken) {
      this.storage.set('token', token)
    }
    this.tokenFields = token ? this.jwtHelper.decodeToken(token) : undefined
    console.debug('AuthService new token fields', this.tokenFields)
    _globalToken = token
    const loggedIn = token != null
    const email = this.tokenFields?.user?.email
    console.debug('AuthService logged in', loggedIn, email)
    this.emailSubject.next(email)
    this.isLoggedInSubject.next(loggedIn)
  }
}
